import React, { useContext, useEffect } from "react";
import logo from "../../assets/images/redlogo.png";
import eye from "../../assets/icons/eye.svg";
import eyeslash from "../../assets/icons/eye-slash.svg";
import { ReactComponent as SmsIcon } from "../../assets/icons/sms.svg";
import { ReactComponent as LockIcon } from "../../assets/icons/lock.svg";
import * as Yup from "yup";
import {
  Alert,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { Box } from "@mui/system";
import LoadingButton from "@mui/lab/LoadingButton";
import { PreferencesContext } from "../../PreferenceContext";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { Login } from "../../service/AuthService";
import { defaultContext, Context } from "../../service/models/Context";
import { AlertType } from "../../shared/AlertType";
import LayoutBg from "../../assets/images/layoutBg.png";

export default function SignIn() {
  const [values, setValues] = React.useState({
    email: "",
    password: "",
    showPassword: false,
  });
  const [errors, setErrors] = React.useState<{
    email?: string;
    password?: string;
  }>({});
  const [loading, setLoading] = React.useState(false);
  const { userContext, updateUserContext } = useContext(PreferencesContext);

  const [showAlert, setAlertMessage] = React.useState({
    isError: false,
    message: "",
  });

  const navigate = useNavigate();

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required("Email is required.")
      .email("Enter a valid email."),
    // password: Yup.string().required("Password is required."),
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters")
      .matches(/[A-Z]/, "Must contain at least one uppercase letter")
      .matches(/[a-z]/, "Must contain at least one lowercase letter")
      .matches(/[0-9]/, "Must contain at least one number")
      .matches(/[@$!%*?&#]/, "Must contain at least one special character"),
  });

  const handleChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setValues({ ...values, [field]: event.target.value });
    };

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };

  async function submitLoginForm(e: React.FormEvent) {
    e.preventDefault();
    try {
      await validationSchema.validate(values, { abortEarly: false });
      setErrors({}); // clear validation errors
    } catch (validationErr: any) {
      type Field = "email" | "password";

      const formErrors: Partial<Record<Field, string>> = {};
      validationErr.inner.forEach((err: any) => {
        if (err.path) {
          formErrors[err.path as Field] = err.message;
        }
      });

      setErrors(formErrors);
      return;
    }

    setLoading(true);
    try {
      const data = {
        email: values.email,
        password: values.password,
        token: userContext?.token,
        uid: userContext?.UID,
      };
      const response: any = await Login(data);
      if (response?.data?.user_id) {
        let curuserInfo: Context = defaultContext;
        curuserInfo.userId = response.data.user_id;
        curuserInfo.userRole = response.data.user_role;
        curuserInfo.email = response.data.email;
        curuserInfo.firstName = response.data.firstName;
        curuserInfo.organizationName = response.data.organizationName;
        curuserInfo.isAlert = true;
        curuserInfo.alertMessage = response?.Message || "Login successful";
        curuserInfo.alertType = AlertType.Success;
        curuserInfo.stakemapUserId = response.data.user_id;
        curuserInfo.UID = response.data?.uid;
        curuserInfo.token = response.data?.token;
        curuserInfo.localToken = response.data?.token;
        updateUserContext(curuserInfo);
        localStorage.setItem("lt", response.data?.token);
        navigate(`/${response.data.token}`);
        // navigate("/");
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: response?.Message || "Login failed",
          alertType: AlertType.Error,
        });
      }
    } catch (error: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: "Sorry, we could not sign you in. Please check details.",
        alertType: AlertType.Error,
      });
    } finally {
      setLoading(false);
      setTimeout(() => {
        setAlertMessage({ isError: false, message: "" });
      }, 7000);
    }
  }

  return (
    <>
      <Grid
        container
        sx={{
          position: "relative",
          backgroundColor: "#E5E5E5",
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          height: "100%",
          width: "100%",
          minWidth: "991px",
          alignItems: "center",
          minHeight: "100vh",

          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url(${LayoutBg})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.1,
            zIndex: 0,
          },

          "& > *": {
            position: "relative",
            zIndex: 1,
          },
        }}
      >
        <Box>
          <Grid size={12} textAlign="center" sx={{ marginBottom: "5px" }}>
            <a href="https://www.stakeholdermaps.com/">
              <img src={logo} alt="logo" style={{ maxWidth: 200 }} />
            </a>
          </Grid>

          <Paper elevation={0} className="paper-singin">
            <Grid sx={{ width: "100%", opacity: 1 }}>
              <form onSubmit={submitLoginForm}>
                <Box className="box-container" sx={{ my: 2 }}>
                  <Paper className="paper-container">
                    {showAlert.isError && (
                      <Grid marginBottom={1}>
                        <Alert severity="error">{showAlert.message}</Alert>
                      </Grid>
                    )}
                    <Typography
                      style={{
                        fontSize: "22px",
                        fontWeight: "500px",
                        // paddingBottom: "15px",
                        marginBottom: "5px",
                        height: "37px",
                      }}
                    >
                      Sign in
                    </Typography>
                    <Typography
                      style={{
                        fontSize: "14px",
                        fontWeight: "400px",
                        color: "rgba(96, 96, 96, 1)",
                        // paddingBottom: "20px",
                      }}
                    >
                      Don’t have an account?{" "}
                      <Link
                        to="/sign-up"
                        style={{ textDecoration: "underline" }}
                      >
                        Sign up
                      </Link>
                    </Typography>
                    <Typography
                      style={{
                        fontSize: "14px",
                        fontWeight: "400px",
                        color: "rgba(96, 96, 96, 1)",
                        paddingBottom: "20px",
                      }}
                    >
                      Note that a Free account can only have one stakeholder
                      map.
                    </Typography>
                    <Typography sx={{ textAlign: "left", fontWeight: "400px" }}>
                      Email
                    </Typography>
                    <TextField
                      sx={{ marginTop: 1 }}
                      fullWidth
                      placeholder="name@company.com"
                      value={values.email}
                      // onChange={handleChange("email")}
                      onChange={(e) =>
                        setValues({
                          ...values,
                          email: e.target.value.toLowerCase(),
                        })
                      }
                      error={!!errors.email}
                      helperText={errors.email}
                      // InputProps={{
                      //   startAdornment: (
                      //     <InputAdornment position="start">
                      //       <SmsIcon stroke={"red"} />
                      //     </InputAdornment>
                      //   ),
                      // }}
                    />

                    <Typography
                      sx={{
                        textAlign: "left",
                        marginTop: 3,
                        fontWeight: "400px",
                      }}
                    >
                      Password
                    </Typography>
                    <TextField
                      sx={{ marginTop: 1 }}
                      fullWidth
                      placeholder="Password"
                      value={values.password}
                      onChange={handleChange("password")}
                      type={values.showPassword ? "text" : "password"}
                      error={!!errors.password}
                      helperText={errors.password}
                      InputProps={{
                        // startAdornment: (
                        //   <InputAdornment position="start">
                        //     <LockIcon />
                        //   </InputAdornment>
                        // ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleClickShowPassword}
                              edge="end"
                              sx={{
                                padding: "0 !important",
                              }}
                            >
                              {values.showPassword ? (
                                <img
                                  loading="lazy"
                                  alt="hide"
                                  className="eye-icon"
                                  src={eyeslash}
                                />
                              ) : (
                                <img
                                  loading="lazy"
                                  alt="show"
                                  className="eye-icon"
                                  src={eye}
                                />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* <Grid size={12} textAlign="right">
                      <Typography style={{ paddingTop: 5 }}>
                        <Link to={"/forgot-password"}>Forgot Password?</Link>
                      </Typography>
                    </Grid> */}

                    <LoadingButton
                      color="primary"
                      sx={{
                        marginTop: 3,
                        backgroundColor: "#4C0808",
                        fontWeight: 400,
                        fontSize: "13px",
                        borderRadius: "4px",
                      }}
                      loading={loading}
                      fullWidth
                      variant="contained"
                      type="submit"
                    >
                      Sign in
                    </LoadingButton>
                    <Typography
                      align="left"
                      marginTop={2.5}
                      sx={{ fontWeight: "400px !important", fontSize: "14px" }}
                    >
                      <Link
                        to={"/forgot-password"}
                        style={{ textDecoration: "underline" }}
                      >
                        Forgot password?
                      </Link>
                    </Typography>
                    {/* <Grid marginTop={3}>
                      <Typography gutterBottom align="center">
                        Don't have an account?&nbsp;
                        <Link to={"/sign-up"}>
                          <b>Sign up</b>
                        </Link>
                      </Typography>
                    </Grid> */}
                  </Paper>
                </Box>
              </form>
            </Grid>
            {/* <Grid className="signin-img-wrapper"></Grid> */}
          </Paper>
        </Box>
      </Grid>
    </>
  );
}
